import React, {
  createContext,
  useContext,
  type ComponentProps,
  type ReactElement,
  type ReactNode,
} from "react";
import { Badge, cn } from "@ndd/ui";
import type {
  AppointmentListItem,
  AppointmentStatus,
} from "../../../../shared/api-types";
import { RD_SCHEDULE_FLAG } from "../../../../shared/features";

type BadgeVariant = ComponentProps<typeof Badge>["variant"];

/** The tones a missed status takes when the agenda splits them (NO-869). */
export type AgendaSplitTone = "cancelled" | "late-cancel" | "no-show" | "rescheduled";

/** The Badge tones an agenda status can take. */
export type AppointmentStatusTone =
  | "info"
  | "success"
  | "warning"
  | "muted"
  | "neutral"
  | AgendaSplitTone;

// Under the always theme (NO-796, approved option B) a routine status
// (Occurred, Checked in, Up next, Rescheduled, or a Healthie status passed
// through, such as "Confirmed by office") is quiet small grey text with no
// pill. Only an exception is coloured: a no-show or a cancellation is a rose
// highlighter with red ink, as Below is (ADR-0004's 2026-10-22 amendment).
// Badge takes a className, so each utility reads a value only the theme
// defines (client/src/styles.css), with the tone's classic Badge value as the
// fallback: with the theme off every status renders exactly as before. Quiet
// text drops the pill's padding, so a row is as tall as its patient name.
//
// With rd-schedule on (NO-869, ADR-0004's 2026-10-30 amendment) each missed
// status gets its own status pair instead: cancelled is the warning pair,
// late cancel is danger soft, no-show is danger soft with a 2px danger edge,
// and rescheduled is muted with a dashed edge. The colours are plain status
// tokens, valid in both themes; only the chip's frame is theme-only.
const QUIET_FRAME = "px-(--status-quiet-px,0.75rem) py-(--status-quiet-py,0.25rem)";
const CHIP_FRAME =
  "rounded-(--status-chip-radius,calc(infinity*1px)) px-(--status-chip-px,0.75rem) py-(--status-chip-py,0.25rem) font-(weight:--status-chip-weight,500)";

export const STATUS_TONE_CLASS: Record<AppointmentStatusTone, string> = {
  info: `${QUIET_FRAME} border-(color:--status-quiet-edge,var(--color-info-soft)) bg-(--status-quiet-fill,var(--color-info-soft)) text-(color:--status-quiet-ink,var(--color-info))`,
  success: `${QUIET_FRAME} border-(color:--status-quiet-edge,var(--color-success-soft)) bg-(--status-quiet-fill,var(--color-success-soft)) text-(color:--status-quiet-ink,var(--color-success))`,
  muted: `${QUIET_FRAME} border-(color:--status-quiet-edge,transparent) bg-(--status-quiet-fill,var(--muted)) text-(color:--status-quiet-ink,var(--muted-foreground))`,
  neutral: `${QUIET_FRAME} border-(color:--status-quiet-edge,var(--color-sand-200)) bg-(--status-quiet-fill,var(--color-sand-100)) text-(color:--status-quiet-ink,var(--color-bark))`,
  warning:
    "rounded-(--highlight-radius,calc(infinity*1px)) px-(--highlight-px,0.75rem) py-(--highlight-py,0.25rem) font-(weight:--highlight-weight,500) border-(color:--highlight-border,var(--color-warning-soft)) bg-(--highlight-fill,var(--color-warning-soft)) bg-(image:--highlight-no) text-(color:--highlight-ink-no,var(--color-warning))",
  cancelled: `${CHIP_FRAME} border-warning-soft bg-warning-soft text-warning`,
  "late-cancel": `${CHIP_FRAME} border-danger-soft bg-danger-soft text-danger`,
  "no-show": `${CHIP_FRAME} border-2 border-danger bg-danger-soft text-danger`,
  rescheduled: `${CHIP_FRAME} border-dashed border-border-strong bg-muted text-muted-strong`,
};

const TONE_BADGE_VARIANT = {
  info: "info",
  success: "success",
  warning: "warning",
  muted: "muted",
  neutral: "neutral",
  cancelled: "warning",
  "late-cancel": "danger",
  "no-show": "danger",
  rescheduled: "muted",
} satisfies Record<AppointmentStatusTone, BadgeVariant>;

const STATUS_BADGE_VARIANTS = {
  scheduled: null,
  checked_in: "info",
  occurred: "success",
  no_show: "warning",
  cancelled: "warning",
  late_cancellation: "warning",
  rescheduled: "muted",
  unknown: "neutral",
} satisfies Record<AppointmentStatus, AppointmentStatusTone | null>;

/** The missed statuses that take their own tone when the agenda splits them. */
export const SPLIT_STATUS_TONES: Readonly<
  Partial<Record<AppointmentStatus, AgendaSplitTone>>
> = {
  no_show: "no-show",
  cancelled: "cancelled",
  late_cancellation: "late-cancel",
  rescheduled: "rescheduled",
};

/**
 * The tone an agenda row shows: the status's split tone when the split is on,
 * otherwise the view's own tone. A status the view hides stays hidden.
 */
export function agendaStatusTone(
  status: AppointmentStatus,
  baseTone: AppointmentStatusTone | null,
  split: boolean
): AppointmentStatusTone | null {
  if (!split || baseTone === null) return baseTone;

  return SPLIT_STATUS_TONES[status] ?? baseTone;
}

const STRUCK_STATUSES: ReadonlySet<AppointmentStatus> = new Set<AppointmentStatus>([
  "cancelled",
  "rescheduled",
]);

/** A cancelled or rescheduled visit won't happen as booked: strike its patient name. */
export function agendaNameStruck(status: AppointmentStatus, split: boolean): boolean {
  return split && STRUCK_STATUSES.has(status);
}

/**
 * Whether the agenda splits its missed statuses: rd-schedule on in the
 * server's /api/features answer. Loading or error (no answer) reads as off.
 */
export function agendaStatusSplitFromFeatures(
  features: Record<string, boolean> | undefined
): boolean {
  return features?.[RD_SCHEDULE_FLAG] === true;
}

// Off unless the route turns it on, so every other render keeps today's look.
const AgendaStatusSplitContext = createContext(false);

/**
 * rd-schedule's agenda status split (NO-869), set once by the /appointments
 * route from the raw /api/features answer, so the route holds no boolean of
 * its own to get wrong.
 */
export function AgendaStatusSplitProvider({
  features,
  children,
}: {
  features: Record<string, boolean> | undefined;
  children: ReactNode;
}): ReactElement {
  return (
    <AgendaStatusSplitContext.Provider value={agendaStatusSplitFromFeatures(features)}>
      {children}
    </AgendaStatusSplitContext.Provider>
  );
}

export function useAgendaStatusSplit(): boolean {
  return useContext(AgendaStatusSplitContext);
}

/**
 * A status on an agenda row: the shared Badge in its classic tone; quiet text
 * or a rose highlighter under the theme; a status pair when split.
 */
export function AppointmentStatusMark({
  tone,
  className,
  children,
}: {
  tone: AppointmentStatusTone;
  className?: string;
  children: ReactNode;
}): ReactElement {
  const variant: BadgeVariant = TONE_BADGE_VARIANT[tone];

  return (
    <Badge variant={variant} className={cn(STATUS_TONE_CLASS[tone], className)}>
      {children}
    </Badge>
  );
}

export function AppointmentRowStatusPill({
  item,
  isUpNext,
  hideOccurred = false,
}: {
  item: AppointmentListItem;
  isUpNext: boolean;
  hideOccurred?: boolean;
}): ReactElement | null {
  const split = useAgendaStatusSplit();

  if (hideOccurred && item.status === "occurred") return null;

  if (isUpNext && item.status === "scheduled") {
    return <AppointmentStatusMark tone="info">Up next</AppointmentStatusMark>;
  }

  const tone = agendaStatusTone(item.status, STATUS_BADGE_VARIANTS[item.status], split);

  if (!tone) return null;

  return <AppointmentStatusMark tone={tone}>{item.statusLabel}</AppointmentStatusMark>;
}

/**
 * NO-428: an automatic Enhancement's Draft finished since the RD last opened
 * this Appointment. Rendered beside the row's status pill, never inside it,
 * so it also shows on rows whose pill is hidden (occurred, quiet statuses).
 * The server sends `draftReady` only when the feature is on for the RD.
 */
export function DraftReadyMark({
  item,
}: {
  item: AppointmentListItem;
}): ReactElement | null {
  if (item.draftReady !== true) return null;

  return <AppointmentStatusMark tone="info">Draft ready</AppointmentStatusMark>;
}

export function AppointmentStatusBadge({
  item,
}: {
  item: AppointmentListItem;
}): ReactElement {
  return (
    <Badge variant={STATUS_BADGE_VARIANTS[item.status] ?? "neutral"}>
      {item.statusLabel}
    </Badge>
  );
}
